import Locales from '@spa/shared/constants/locales';
import { getLanguageFromPath } from '@spa/shared/utils';
import { setHistoryPath } from '@spa/shared/utils/history';
import { getMapQuery } from '@spa/shared/utils/queryParams';
import { UNIVERSITIES, NEIGHBOURHOODS, CITIES } from '@spa/shared/constants/locationTypes';

const LATITUDE_MARGIN = 0.0185;
const LONGITUDE_MARGIN = 0.016;

export const getSearchParametersForUniversity = ({ latitude, longitude, bounding_box, name, city }) => {
  const center = { latitude, longitude };

  if (bounding_box) {
    const { north, east, south, west } = bounding_box;

    return { ne: `${north},${east}`, sw: `${south},${west}`, city, university: name, center };
  }

  const north = latitude + LATITUDE_MARGIN;
  const east = longitude + LONGITUDE_MARGIN;
  const south = latitude - LATITUDE_MARGIN;
  const west = longitude - LONGITUDE_MARGIN;

  return { ne: `${north},${east}`, sw: `${south},${west}`, city, university: name, center };
};

export const getSearchParametersForNeighbourhood = ({ geometry, city, slug }) => {
  const { north, east, south, west } = geometry.reduce((acc, currentValue) => {
    const { latitude, longitude } = currentValue;

    return {
      north: acc.north ? Math.max(latitude, acc.north) : latitude,
      east: acc.east ? Math.max(longitude, acc.east) : longitude,
      south: acc.south ? Math.min(latitude, acc.south) : latitude,
      west: acc.west ? Math.min(longitude, acc.west) : longitude
    };
  });

  return { ne: `${north},${east}`, sw: `${south},${west}`, city, neighbourhood: slug };
};

export const universityToSearch = ({
  id,
  configuration: { city_code: city, slug } = {},
  geo: { latitude, longitude, bounding_box } = {}
}) => {
  const attributes = {
    id,
    city,
    type: UNIVERSITIES,
    name: slug,
    latitude,
    longitude,
    bounding_box
  };

  return getSearchParametersForUniversity(attributes);
};

export const neighbourhoodToSearch = ({
  configuration: { city_code: city, slug } = {},
  geo_shape: { items: geometry } = {}
}) => getSearchParametersForNeighbourhood({ city, geometry, slug });

const cityToSearch = ({
  code: city,
  configuration: { location: { default_bounding_box: { north, east, south, west } = {} } = {} } = {}
}) => ({ city, ne: `${north},${east}`, sw: `${south},${west}` });

export const getCountry = location => {
  return location.city ? location.city.configuration.country_code : location.configuration.country_code;
};

export const getCity = location => {
  return location.city ? location.city.configuration.slug : location.configuration.slug;
};

export const locationToSearch = ({ type, ...attributes }) => {
  if (type === CITIES) {
    return cityToSearch(attributes);
  }

  if (type === UNIVERSITIES) {
    return universityToSearch(attributes);
  }

  if (type === NEIGHBOURHOODS) {
    return neighbourhoodToSearch(attributes);
  }

  throw Error('unknown location type');
};

export const setPath = (history, location, isResidences = false) => {
  const { query } = history;

  const path = getUrlPath({ ...location, pathname: history.asPath, isResidences });
  const state = locationToSearch(location);
  const isSamePath = query.university === state.university && query.neighbourhood === state.neighbourhood;
  const view = window.location.search.indexOf('view=map') > -1 ? { view: 'map' } : {};
  const mapQuery = isSamePath ? getMapQuery({ ...query, ...view }, state) : getMapQuery({ ...state, ...view }, query);

  setHistoryPath(history, path, { ...state, ...mapQuery }, !isSamePath);
};

const getUrlPath = ({ pathname = '', type, configuration: { slug }, city: cityConfig, isResidences = false }) => {
  const lang = getLanguageFromPath(pathname);
  const langToPath = lang.length === 2 ? `/${lang}` : lang;
  const city = cityConfig ? cityConfig.configuration.slug : '';
  const accommodationOrResidences = isResidences ? 'residences' : 'accommodation';

  switch (type) {
    case CITIES:
      return `${langToPath}/${accommodationOrResidences}/${slug}`;
    case NEIGHBOURHOODS:
      return `${langToPath}/${accommodationOrResidences}/${city}/neighbourhood/${slug}`;
    case UNIVERSITIES:
      return `${langToPath}/${accommodationOrResidences}/${city}/university/${slug}`;
    default:
      throw new Error('location type unknown');
  }
};

export const locationToItem = (data, type, locale) => {
  const translations = data.metadata?.name_translations.items;

  const nameTranslation =
    translations.find(({ locale_code: localeCode }) => localeCode === locale) ||
    translations.find(({ locale_code: localeCode }) => localeCode === Locales.EN);

  const vars = {
    attributes: {
      name_translation: nameTranslation.text,
      name: data.configuration.slug,
      city: data.code || data.configuration.city_code
    },
    id: data.id || data.code,
    type: type
  };

  return vars;
};

export const locationTranslation = (location, locale) => {
  const { attributes: { name_translation: translation = '' } = {} } = locationToItem(location, location.type, locale);

  return translation;
};

export const getSubdomainFromHost = host => {
  const [firstSubdomain, secondSubdomain] = host.split('.');

  return firstSubdomain === 'www' ? secondSubdomain : firstSubdomain;
};

export const isSameLocation = (locationA, locationB) => {
  const { city: payloadCity, university, neighbourhood } = locationA;
  const {
    city,
    configuration: { slug }
  } = locationB;
  if (university || neighbourhood) {
    return city && payloadCity === city.configuration.slug && (university === slug || neighbourhood === slug);
  }

  return payloadCity === slug;
};
